import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks';

/**
 * ProtectedRoute — wraps any routes that require authentication.
 *
 * Behaviour:
 *  - While the initial session is loading, renders a branded full-screen spinner
 *    to avoid a flash of the login page for users who are already signed in.
 *  - If the user is NOT authenticated, redirects to /login and saves the
 *    intended destination so we can redirect back after sign-in.
 *  - If the user IS authenticated, renders the child route via <Outlet />.
 *
 * Usage in App.tsx:
 *  <Route element={<ProtectedRoute />}>
 *    <Route path="/" element={<DashboardPage />} />
 *    ...
 *  </Route>
 */
function ProtectedRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // 1. Session still being hydrated — show a non-jarring spinner
  if (loading) {
    return (
      <div
        role="status"
        aria-label="Loading"
        className="min-h-screen flex flex-col items-center justify-center bg-brand-950 gap-4"
      >
        <div className="w-12 h-12 rounded-2xl bg-brand-500 flex items-center justify-center animate-pulse-soft shadow-lg">
          <span className="text-2xl" aria-hidden="true">🐾</span>
        </div>
        <span className="text-white/40 text-sm animate-pulse">Loading…</span>
      </div>
    );
  }

  // 2. Not authenticated — redirect to /login, preserving the intended URL
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 3. Authenticated — render the nested route
  return <Outlet />;
}

export default ProtectedRoute;
